import { useEffect, useMemo, useState } from 'react';

import { AttendanceIcon, MapPinIcon, SparkIcon } from '@/components/Icons';
import { Modal } from '@/components/Modal';
import { useAttendanceState } from '@/context/AttendanceContext';
import { useSessionState } from '@/context/SessionContext';
import { useStaffDirectoryState } from '@/context/StaffContext';
import { getInitials, formatShortDate, formatShortDateTime } from '@/lib/format';
import { captureCurrentLocation, isLocationPermissionError } from '@/lib/location';
import { tintStyle } from '@/lib/style';
import type { StaffMember } from '@/lib/types';

function StaffChip({
  staff,
  selected,
  active,
  onClick,
}: {
  staff: StaffMember;
  selected: boolean;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`list-item interactive centered${selected ? ' active' : ''}`}
      type="button"
      onClick={onClick}
      >
      <div className="media-row">
        <div className="avatar tinted" style={tintStyle(staff.color)}>
          {getInitials(staff.name)}
        </div>
        <div>
          <strong>{staff.name}</strong>
          <span>{staff.role}</span>
        </div>
      </div>
      <span className="badge soft">{active ? 'On shift' : 'Available'}</span>
    </button>
  );
}

function SessionCard({
  id,
  active,
  staffName,
  staffRole,
  clockInAt,
  clockOutAt,
  clockInLocation,
  clockOutLocation,
}: {
  id: string;
  active: boolean;
  staffName: string;
  staffRole: string;
  clockInAt: string;
  clockOutAt: string | null;
  clockInLocation: { latitude: number; longitude: number; accuracy: number | null };
  clockOutLocation: { latitude: number; longitude: number; accuracy: number | null } | null;
}) {
  return (
    <div className="list-item" key={id}>
      <div>
        <strong>
          {staffName} · {staffRole}
        </strong>
        <span>{formatShortDate(clockInAt)}</span>
        <p>
          In {formatShortDateTime(clockInAt)} {clockOutAt ? `· Out ${formatShortDateTime(clockOutAt)}` : '· Active'}
        </p>
        <p>
          In pin {clockInLocation.latitude.toFixed(4)}, {clockInLocation.longitude.toFixed(4)}{' '}
          {clockInLocation.accuracy !== null ? `· accuracy ±${Math.round(clockInLocation.accuracy)}m` : ''}
        </p>
        {clockOutLocation ? (
          <p>
            Out pin {clockOutLocation.latitude.toFixed(4)}, {clockOutLocation.longitude.toFixed(4)}{' '}
            {clockOutLocation.accuracy !== null ? `· accuracy ±${Math.round(clockOutLocation.accuracy)}m` : ''}
          </p>
        ) : null}
      </div>
      <span className="badge soft">{active ? 'Active' : 'Completed'}</span>
    </div>
  );
}

export function AttendancePage() {
  const { sessionRole, sessionStaffId, sessionUsername } = useSessionState();
  const { isReady: attendanceReady, staffMembers, sessions, clockIn, clockOut, refreshAttendance } =
    useAttendanceState();
  const { isReady: staffReady } = useStaffDirectoryState();
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(sessionStaffId ?? null);
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationPermissionMessage, setLocationPermissionMessage] = useState<string | null>(null);

  const isStaffAccount = sessionRole === 'staff';

  useEffect(() => {
    if (isStaffAccount && sessionStaffId) {
      setSelectedStaffId(sessionStaffId);
      return;
    }

    if (!isStaffAccount && staffMembers.length > 0) {
      setSelectedStaffId((current) => {
        if (current && staffMembers.some((member) => member.id === current)) {
          return current;
        }

        return staffMembers[0].id;
      });
    }
  }, [isStaffAccount, sessionStaffId, staffMembers]);

  const resolvedStaffId = useMemo(() => {
    if (isStaffAccount) {
      if (sessionStaffId) {
        return sessionStaffId;
      }

      if (sessionUsername) {
        const matchedStaff = staffMembers.find((member) => member.username === sessionUsername);
        if (matchedStaff) {
          return matchedStaff.id;
        }
      }
    }

    if (selectedStaffId && staffMembers.some((member) => member.id === selectedStaffId)) {
      return selectedStaffId;
    }

    return staffMembers[0]?.id ?? null;
  }, [isStaffAccount, selectedStaffId, sessionStaffId, sessionUsername, staffMembers]);

  const selectedStaff = useMemo(() => {
    if (!resolvedStaffId) {
      return null;
    }

    return staffMembers.find((member) => member.id === resolvedStaffId) ?? null;
  }, [resolvedStaffId, staffMembers]);

  const scopedStaffMembers = useMemo(() => {
    if (isStaffAccount && sessionStaffId) {
      return staffMembers.filter((member) => member.id === sessionStaffId);
    }

    return staffMembers;
  }, [isStaffAccount, sessionStaffId, staffMembers]);

  const selectedSessions = useMemo(() => {
    if (!resolvedStaffId) {
      return sessions;
    }

    return sessions.filter((session) => session.staffId === resolvedStaffId);
  }, [resolvedStaffId, sessions]);

  const completedSessions = sessions.filter((session) => session.clockOutAt !== null);
  const completedSelectedSessions = selectedSessions.filter((session) => session.clockOutAt !== null);
  const activeSessions = sessions.filter((session) => session.clockOutAt === null);
  const activeStaffCount = new Set(activeSessions.map((session) => session.staffId)).size;
  const selectedActiveSession = resolvedStaffId
    ? sessions.find((session) => session.staffId === resolvedStaffId && session.clockOutAt === null) ?? null
    : null;
  const canClockIn = Boolean(resolvedStaffId) && !selectedActiveSession && !isSubmitting;
  const canClockOut = Boolean(resolvedStaffId) && Boolean(selectedActiveSession) && !isSubmitting;
  const visibleSessionCount = resolvedStaffId
    ? completedSelectedSessions.length
    : completedSessions.length;

  useEffect(() => {
    setFeedback(null);
  }, [resolvedStaffId]);

  function handleAttendanceError(error: unknown, fallbackMessage: string) {
    if (isLocationPermissionError(error)) {
      setLocationPermissionMessage(
        error instanceof Error && error.message
          ? error.message
          : 'Location permission is required to record attendance.'
      );
      return;
    }

    setFeedback({
      kind: 'error',
      message: error instanceof Error && error.message ? error.message : fallbackMessage,
    });
  }

  async function handleClockIn() {
    if (isSubmitting || selectedActiveSession) {
      if (selectedActiveSession) {
        setFeedback({
          kind: 'error',
          message: `${selectedStaff?.name ?? 'This staff member'} is already clocked in.`,
        });
      }
      return;
    }

    if (!resolvedStaffId) {
      setFeedback({
        kind: 'error',
        message: 'Choose a staff member before clocking in.',
      });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);
    setLocationPermissionMessage(null);

    try {
      const location = await captureCurrentLocation();
      await clockIn(resolvedStaffId, location);
      await refreshAttendance();
      setFeedback({
        kind: 'success',
        message: `${selectedStaff?.name ?? 'Staff member'} clocked in successfully.`,
      });
    } catch (error) {
      handleAttendanceError(error, 'Could not clock in right now.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleClockOut() {
    if (isSubmitting || !selectedActiveSession) {
      if (!selectedActiveSession) {
        setFeedback({
          kind: 'error',
          message: `${selectedStaff?.name ?? 'This staff member'} does not have an active clock in.`,
        });
      }
      return;
    }

    if (!resolvedStaffId) {
      setFeedback({
        kind: 'error',
        message: 'Choose a staff member before clocking out.',
      });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);
    setLocationPermissionMessage(null);

    try {
      const location = await captureCurrentLocation();
      await clockOut(resolvedStaffId, location);
      await refreshAttendance();
      setFeedback({
        kind: 'success',
        message: `${selectedStaff?.name ?? 'Staff member'} clocked out successfully.`,
      });
    } catch (error) {
      handleAttendanceError(error, 'Could not clock out right now.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!attendanceReady || !staffReady) {
    return <div className="section">Loading attendance...</div>;
  }

  return (
    <div className="section page-grid">
      <section className="hero-layout">
        <div className="hero-copy">
          <span className="eyebrow">
            <AttendanceIcon size={14} />
            <strong>Attendance</strong>
            <span>{activeStaffCount} active right now</span>
          </span>

          <h1 className="display-title">
            <span>Shift control</span>
          </h1>

          <p className="copy">Clock in, clock out, and review the live timeline.</p>

          <div className="button-row hero-actions">
            <button className="primary-button" type="button" onClick={handleClockIn} disabled={!canClockIn}>
              <SparkIcon size={16} />
              Clock in
            </button>
            <button className="secondary-button" type="button" onClick={handleClockOut} disabled={!canClockOut}>
              <MapPinIcon size={16} />
              Clock out
            </button>
          </div>

          {feedback ? <div className={`notice feedback-stack ${feedback.kind}`}>{feedback.message}</div> : null}
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Staff members</div>
            <div className="stat-value">{scopedStaffMembers.length}</div>
            <p className="stat-hint">The team available for today&apos;s shift</p>
          </div>
          <div className="stat-card highlight">
            <div className="stat-label">Active shifts</div>
            <div className="stat-value">{activeStaffCount}</div>
            <p className="stat-hint">Members currently clocked in</p>
          </div>
          <div className="stat-card">
            <div className="stat-label">Sessions</div>
            <div className="stat-value">{visibleSessionCount}</div>
            <p className="stat-hint">
              {resolvedStaffId ? 'Records for the selected staff member' : 'All clock in and clock out records'}
            </p>
          </div>
          <div className="stat-card">
            <div className="stat-label">Selection</div>
            <div className="stat-value">{selectedStaff ? getInitials(selectedStaff.name) : '--'}</div>
            <p className="stat-hint">{selectedStaff?.name ?? 'Choose a staff member'}</p>
          </div>
        </div>
      </section>

      <section className="content-grid">
        <div className="stack">
          <div className="panel">
            <div className="section-heading">
              <div>
                <h2>Team selector</h2>
                <p>Pick a staff member to see their active shift and location history.</p>
              </div>
            </div>

            <div className="list">
              {scopedStaffMembers.map((staff) => {
                const active = sessions.some((session) => session.staffId === staff.id && session.clockOutAt === null);

                return (
                  <StaffChip
                    key={staff.id}
                    staff={staff}
                    selected={staff.id === resolvedStaffId}
                    active={active}
                    onClick={() => {
                      if (!isStaffAccount) {
                        setSelectedStaffId(staff.id);
                        setFeedback(null);
                      } else if (sessionStaffId) {
                        setSelectedStaffId(sessionStaffId);
                      }
                    }}
                  />
                );
              })}
            </div>
          </div>

          {selectedStaff && selectedActiveSession ? (
            <div className="panel">
              <div className="section-heading">
                <div>
                  <h2>Current shift</h2>
                  <p>Clock-in state for the selected staff member.</p>
                </div>
                <div className="badge soft">On shift</div>
              </div>

              <div className="list">
                <div className="list-item">
                  <div>
                    <strong>{selectedStaff.name}</strong>
                    <span>{selectedStaff.role}</span>
                    <p>Clocked in {formatShortDateTime(selectedActiveSession.clockInAt)}</p>
                  </div>
                  <span className="badge soft">Active</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {completedSelectedSessions.length > 0 ? (
          <div className="stack">
            <div className="panel">
              <div className="section-heading">
                <div>
                  <h2>Session history</h2>
                  <p>Completed shifts for the selected staff member.</p>
                </div>
              </div>

              <div className="list">
                {completedSelectedSessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    id={session.id}
                    active={false}
                    staffName={session.staffName}
                    staffRole={session.staffRole}
                    clockInAt={session.clockInAt}
                    clockOutAt={session.clockOutAt}
                    clockInLocation={session.clockInLocation}
                    clockOutLocation={session.clockOutLocation}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </section>

      <Modal
        open={locationPermissionMessage !== null}
        title="Location permission required"
        description="Attendance needs your location to record clock in and clock out."
        onClose={() => {
          setLocationPermissionMessage(null);
        }}>
        <div className="stack">
          <p>{locationPermissionMessage}</p>
          <p className="copy">
            Open your browser settings, allow location access for this site, then try clocking in or out again.
          </p>
          <div className="button-row">
            <button
              className="primary-button"
              type="button"
              onClick={() => {
                setLocationPermissionMessage(null);
              }}>
              Got it
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
